import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Mutation, Query } from 'react-apollo'
import { adopt } from 'react-adopt'

import { dataByTypename } from '../../utils/apollo-helpers'
import {
  LOGIN,
  MFA_LOGIN,
  LOGOUT,
  CUSTOMER,
  AUTHENTICATE
} from './Auth.graphql'

import { identify as heapIdentify, resetIdentity as heapResetIdentity } from '../../third-party/HeapAnalytics'
import { useFlag } from '../FeatureFlag/use-flag'

const UNVERIFIED_CUSTOMER_SOURCES = Object.freeze([
  'MANUAL_CREATE_ACCOUNT',
  'SAVE_CREDIT_CARD',
  'LOYALTY_SIGNUP'
])

const initialUnverifiedCustomer = Object.freeze({
  guid: null,
  signupSource: null
})

const AuthContext = React.createContext({
  authenticated: false,
  error: null,
  loading: true,
  user: null,
  unverifiedCustomer: initialUnverifiedCustomer
})

export const Login = ({ children }) => (
  <Mutation
    mutation={LOGIN}
    refetchQueries={[{ query: CUSTOMER }]}
    awaitRefetchQueries
  >
    {(mutate, result) => {
      if (result.data) {
        const { LoginError } = dataByTypename(result.data.login)
        return children(mutate, {...result, error: LoginError || result.error})
      }
      return children(mutate, result)
    }}
  </Mutation>
)

Login.propTypes = {
  children: PropTypes.func
}

const trackHeapIdentity = (data) => {
  if (data && data.customer && data.customer.guid) {
    heapIdentify(data.customer.guid)
  } else {
    heapResetIdentity()
  }
}

// eslint-disable-next-line react/prop-types
const getCustomerMutation = props => ({ render }) => (
  <Mutation
    {...props}
    refetchQueries={[{ query: CUSTOMER }]}
    awaitRefetchQueries
  >
    {(mutate, result) => render({ mutate, result })}
  </Mutation>
)

const Customer = ({ render }) => {
  const shouldUseHeap = useFlag('oo-v3-heapanalytics')
  return (
    <Query query={CUSTOMER}
      onCompleted={(data) => {
        if (shouldUseHeap) {
          trackHeapIdentity(data)
        }
      }}
    >{result => render({ result })}</Query>
  )
}
Customer.propTypes = {
  render: PropTypes.func
}

const AuthContainer = adopt({
  logout: getCustomerMutation({ mutation: LOGOUT }),
  login: getCustomerMutation({ mutation: LOGIN }),
  mfaLogin: getCustomerMutation({ mutation: MFA_LOGIN }),
  authenticate: getCustomerMutation({
    mutation: AUTHENTICATE
  }),
  customer: Customer
})

const getStatus = (loginResult, customerResult, mfaLoginResult) => {
  if (loginResult.loading || customerResult.loading || mfaLoginResult.loading) {
    return { loading: true }
  }

  if (customerResult.error) return { error: customerResult.error }
  if (loginResult.error) return { error: loginResult.error }
  if (mfaLoginResult.error) return { error: mfaLoginResult.error }

  if (loginResult.called) {
    const { LoginError } = dataByTypename(loginResult.data.login)
    if (LoginError) {
      return { error: LoginError }
    }
  }

  if (mfaLoginResult.called) {
    const { LoginError } = dataByTypename(mfaLoginResult.data.mfaLogin)
    if (LoginError) {
      return { error: LoginError }
    }
  }

  const user = customerResult && customerResult.data && customerResult.data.customer
  return { user }
}

export const AuthProvider = React.memo(({ children }) => {
  const [unverifiedCustomer, setUnverifiedCustomerValue] = useState(initialUnverifiedCustomer)
  const [mfaState, setMfaState] = useState({
    mfaRequiresVerification: false,
    challengeToken: '',
    mfaType: '',
    maskedRecipient: ''
  })

  const setUnverifiedCustomer = useCallback(newState => {
    if (!newState.guid) {
      console.error('setUnverifiedCustomer must include a guid')
    }

    if (!newState.signupSource) {
      console.error('setUnverifiedCustomer must include a signupSource')
    }

    if (!UNVERIFIED_CUSTOMER_SOURCES.includes(newState.signupSource)) {
      console.error(`invalid signupSource '${newState.signupSource}'`)
    }

    setUnverifiedCustomerValue(newState)
  }, [])

  const clearUnverifiedCustomer = useCallback(() => {
    setUnverifiedCustomerValue(initialUnverifiedCustomer)
  }, [])

  return (
    <AuthContainer>
      {({ logout, login, mfaLogin, customer, authenticate }) => {
        const { user, error, loading } = getStatus(
          login.result,
          customer.result,
          mfaLogin.result
        )
        const context = {
          authenticated: Boolean(user),
          error,
          loading,
          login: login.mutate,
          mfaLogin: mfaLogin.mutate,
          logout: logout.mutate,
          authenticate: authenticate.mutate,
          clearUnverifiedCustomer,
          setUnverifiedCustomer,
          unverifiedCustomer,
          user,
          setMfaState,
          ...mfaState
        }

        return (
          <AuthContext.Provider value={context}>
            {children}
          </AuthContext.Provider>
        )
      }}
    </AuthContainer>
  )
})

AuthProvider.propTypes = {
  children: PropTypes.node
}

export const AuthConsumer = ({ children }) => {
  return (
    <AuthContext.Consumer>{context => children(context)}</AuthContext.Consumer>
  )
}

AuthConsumer.propTypes = {
  children: PropTypes.func
}

export const useAuth = () => React.useContext(AuthContext)
